import React from "react"
import { Badge } from "../../components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { 
  FileText, 
  Cookie, 
  Shield, 
  Link as LinkIcon, 
  AlertTriangle, 
  Eye, 
  Scale,
  Globe,
  Users,
  Lock
} from "lucide-react"
import SEO from "../SEO"

export default function TermsConditions() {
  const termsConditionsSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms and Conditions - Pranaam Hospitals",
    "description": "Pranaam Hospitals Terms and Conditions outlining the rules and regulations for the use of our website and services.",
    "url": "https://pranaam.pranaamhospitals.in/patient-care/terms-conditions"
  }

  return (
    <>
      <SEO
        title="Terms and Conditions | Pranaam Hospitals - Website Usage Rules"
        description="Read Pranaam Hospitals' Terms and Conditions outlining the rules and regulations for using our website and services."
        canonical="https://pranaam.pranaamhospitals.in/patient-care/terms-conditions"
        schema={termsConditionsSchema}
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-primary/10 to-primary/5 py-16">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 px-3 py-1">Patient Care</Badge>
              <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl">
                Terms and <span className="text-primary">Conditions</span>
              </h1>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Welcome to Pranaam Hospitals! These terms and conditions outline the rules and regulations for the use of Pranaam Hospitals Pvt Ltd's Website, located at www.pranaamhospitals.com.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto space-y-8">
              
              {/* Introduction */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-6 h-6 text-primary" />
                    Agreement to Terms
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                    <p className="text-yellow-800">
                      <strong>Important:</strong> By accessing this website we assume you accept these terms and conditions. Do not continue to use Pranaam Hospitals if you do not agree to take all of the terms and conditions stated on this page.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Terminology */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-6 h-6 text-primary" />
                    Terminology
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">
                    The following terminology applies to these Terms and Conditions, Privacy Statement and Disclaimer Notice and all Agreements:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-2">"Client", "You" and "Your"</h4>
                      <p className="text-blue-700 text-sm">Refers to you, the person log on this website and compliant to the Company's terms and conditions.</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-2">"The Company", "We", "Our" and "Us"</h4>
                      <p className="text-green-700 text-sm">Refers to our Company - Pranaam Hospitals Pvt Ltd.</p>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm mt-4">
                    All terms refer to the offer, acceptance and consideration of payment necessary to undertake the process of our assistance to the Client in the most appropriate manner for meeting the Client's needs in respect of provision of the Company's stated services.
                  </p>
                </CardContent>
              </Card>

              {/* Cookies */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Cookie className="w-6 h-6 text-primary" />
                    Cookies
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">
                    We employ the use of cookies. By accessing Pranaam Hospitals, you agreed to use cookies in agreement with the Pranaam Hospitals Pvt Ltd's Privacy Policy.
                  </p>
                  <p className="text-gray-700">
                    Most interactive websites use cookies to let us retrieve the user's details for each visit. Cookies are used by our website to enable the functionality of certain areas to make it easier for people visiting our website. Some of our affiliate/advertising partners may also use cookies.
                  </p>
                </CardContent>
              </Card>

              {/* License */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-6 h-6 text-primary" />
                    License
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-700">
                    Unless otherwise stated, Pranaam Hospitals Pvt Ltd and/or its licensors own the intellectual property rights for all material on Pranaam Hospitals. All intellectual property rights are reserved. You may access this from Pranaam Hospitals for your own personal use subjected to restrictions set in these terms and conditions.
                  </p>
                  
                  <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                    <h4 className="font-semibold text-red-800 mb-3">You must not:</h4>
                    <ul className="space-y-2 text-red-700">
                      <li>• Republish material from Pranaam Hospitals</li>
                      <li>• Sell, rent or sub-license material from Pranaam Hospitals</li>
                      <li>• Reproduce, duplicate or copy material from Pranaam Hospitals</li>
                      <li>• Redistribute content from Pranaam Hospitals</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* User Comments */}
              <Card>
                <CardHeader>
                  <CardTitle>User Comments</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-700">
                    Parts of this website offer an opportunity for users to post and exchange opinions and information in certain areas of the website. Pranaam Hospitals Pvt Ltd does not filter, edit, publish or review Comments prior to their presence on the website.
                  </p>
                  
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <p className="text-orange-700">
                      <strong>Important:</strong> Comments do not reflect the views and opinions of Pranaam Hospitals Pvt Ltd, its agents and/or affiliates. Pranaam Hospitals Pvt Ltd reserves the right to monitor all Comments and to remove any Comments which can be considered inappropriate, offensive or causes breach of these Terms and Conditions.
                    </p>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-3">You warrant and represent that:</h4>
                    <ul className="space-y-2 text-blue-700 text-sm">
                      <li>• You are entitled to post the Comments on our website and have all necessary licenses and consents to do so</li>
                      <li>• The Comments do not invade any intellectual property right, including copyright, patent or trademark of any third party</li>
                      <li>• The Comments do not contain any defamatory, libelous, offensive, indecent or otherwise unlawful material</li>
                      <li>• The Comments will not be used to solicit or promote business or custom or present commercial activities or unlawful activity</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Hyperlinking */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LinkIcon className="w-6 h-6 text-primary" />
                    Hyperlinking to Our Content
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">The following organizations may link to our Website without prior written approval:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ul className="space-y-2 text-gray-700">
                        <li>• Government agencies</li>
                        <li>• Search engines</li>
                        <li>• News organizations</li>
                      </ul>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Online directory distributors</li>
                        <li>• System wide Accredited Businesses</li>
                      </ul>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">We may consider and approve other link requests from:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ul className="space-y-2 text-gray-700">
                        <li>• Consumer and/or business information sources</li>
                        <li>• Dot.com community sites</li>
                        <li>• Associations or groups representing charities</li>
                        <li>• Online directory distributors</li>
                      </ul>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Internet portals</li>
                        <li>• Accounting, law and consulting firms</li>
                        <li>• Educational institutions and trade associations</li>
                      </ul>
                    </div>
                  </div>

                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-green-700">
                      <strong>Link Request Process:</strong> If you are interested in linking to our website, you must inform us by sending an e-mail to Pranaam Hospitals Pvt Ltd. Please include your name, organization name, contact information, and URLs. Wait 2-3 weeks for a response.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* iFrames */}
              <Card>
                <CardHeader>
                  <CardTitle>iFrames</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                    <p className="text-red-700">
                      Without prior approval and written permission, you may not create frames around our Webpages that alter in any way the visual presentation or appearance of our Website.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Content Liability */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-primary" />
                    Content Liability
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    We shall not be hold responsible for any content that appears on your Website. You agree to protect and defend us against all claims that is rising on your Website. No link(s) should appear on any Website that may be interpreted as libelous, obscene or criminal, or which infringes, otherwise violates, or advocates the infringement or other violation of, any third party rights.
                  </p>
                </CardContent>
              </Card>

              {/* Privacy */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-6 h-6 text-primary" />
                    Your Privacy
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    Please read our Privacy Policy for detailed information about how we collect, use, and protect your personal information.
                  </p>
                </CardContent>
              </Card>

              {/* Reservation of Rights */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="w-6 h-6 text-primary" />
                    Reservation of Rights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">
                    We reserve the right to request that you remove all links or any particular link to our Website. You approve to immediately remove all links to our Website upon request. We also reserve the right to amend these terms and conditions and its linking policy at any time.
                  </p>
                  <p className="text-gray-700">
                    By continuously linking to our Website, you agree to be bound to and follow these linking terms and conditions.
                  </p>
                </CardContent>
              </Card>

              {/* Disclaimer */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Scale className="w-6 h-6 text-primary" />
                    Disclaimer
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-700">
                    We do not ensure that the information on this website is correct, we do not warrant its completeness or accuracy; nor do we promise to ensure that the website remains available or that the material on the website is kept up to date.
                  </p>
                  
                  <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                    <h4 className="font-semibold text-yellow-800 mb-3">Nothing in this disclaimer will:</h4>
                    <ul className="space-y-2 text-yellow-700">
                      <li>• Limit or exclude our or your liability for death or personal injury</li>
                      <li>• Limit or exclude our or your liability for fraud or fraudulent misrepresentation</li>
                      <li>• Limit any of our or your liabilities in any way that is not permitted under applicable law</li>
                      <li>• Exclude any of our or your liabilities that may not be excluded under applicable law</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-blue-700">
                      <strong>Free Service Disclaimer:</strong> As long as the website and the information and services on the website are provided free of charge, we will not be liable for any loss or damage of any nature.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Contact for Concerns */}
              <Card className="border-l-4 border-primary">
                <CardHeader>
                  <CardTitle className="text-primary">Questions or Concerns?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    If you have any questions about these Terms and Conditions, please contact us through our website or customer service channels.
                  </p>
                </CardContent>
              </Card>

            </div>
          </div>
        </section>
      </div>
    </>
  )
}